import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import {
  AuthService,
  DataService,
  ToasterService
} from '../../shared/services';
import { Router } from '@angular/router';
import { GridOptions } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent } from '../../shared';
import { BreadCrumbService } from '../../shared/services/breadcrumb.service';
import { isNumber } from 'lodash';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html'
})
export class RoutingScheduleComponent implements OnInit {
  @ViewChild(GridComponent, { static: true }) grid?: GridComponent;

  selectedPlan: any[] = [];
  isDefaultPlanSelected = false;
  // Available items
  items: any[] = [];
  selectedNodes: any[] = [];
  // UI flag
  isLoading = false;
  // Local grid options
  gridOptions?: GridOptions;
  // Modal ref pointer
  bsModalRef?: BsModalRef;
  selectedCalendarDate?: string; // 'YYYY-MM-DD'
  @ViewChild('calendarStartDateTpl') calendarStartDateTpl?: TemplateRef<any>;

  topRow: any[] = [];
  weekDates: { display_date: string; full_date: string }[] = []; // Array to store week dates in dd/mm format
  maxWeekTotal = 0;
  siteData: any = null; // Store site data to avoid duplicate API calls

  constructor(
    private translate: TranslateService,
    private data: DataService,
    private toastr: ToasterService,
    public auth: AuthService,
    public router: Router,
    public breadCrumbService: BreadCrumbService,
    private bsModalService: BsModalService
  ) {}

  /**
   * Calculate week dates starting from sched_cal_start_date if it's Monday, otherwise from the next Monday
   * @param schedCalStartDate The calendar start date from the site
   * @returns Array of week dates in dd/mm format and full date in YYYY-MM-DD format
   */
  private calculateWeekDates(
    schedCalStartDate: string
  ): { display_date: string; full_date: string }[] {
    // dates as array of objects with display_date and full date in YYYY-MM-DD format
    const dates: { display_date: string; full_date: string }[] = [];
    let currentDate = new Date(schedCalStartDate);

    // Check if sched_cal_start_date is already Monday (getDay() returns 1 for Monday)
    if (currentDate.getDay() === 1) {
      // If it's already Monday, start W1 with that date
      // No need to move to next Monday
    } else {
      // Find the next Monday after sched_cal_start_date
      const daysUntilMonday = (8 - currentDate.getDay()) % 7;
      currentDate.setDate(currentDate.getDate() + daysUntilMonday);
    }

    // Generate 52 weeks starting from the current date (either sched_cal_start_date if Monday, or next Monday)
    for (let i = 0; i < 52; i++) {
      const weekDate = new Date(currentDate);
      weekDate.setDate(currentDate.getDate() + i * 7);

      const day = weekDate.getDate().toString().padStart(2, '0');
      const month = (weekDate.getMonth() + 1).toString().padStart(2, '0');
      dates.push({
        display_date: `${day}/${month}`,
        full_date: weekDate.toISOString().split('T')[0]
      });
    }

    return dates;
  }

  /**
   * Get week field name for a given week index
   * @param weekIndex 0-based week index
   * @returns Week field name (W1, W2, etc.)
   */
  private getWeekFieldName(weekIndex: number): string {
    return `week_${weekIndex + 1}`;
  }

  /**
   * Refresh the grid columns with updated week dates
   */
  private refreshGridColumns(): void {
    if (this.grid && this.grid.gridApi && this.gridOptions?.columnDefs) {
      // Remove existing week columns if any
      const fixedColumns = this.gridOptions.columnDefs.filter(
        (col: any) => !col.field || !col.field.startsWith('week_')
      );

      // Add new week columns
      const weekColumns = this.weekDates.map((dateObj, index) => ({
        headerName: dateObj.display_date,
        headerValue: dateObj.full_date,
        field: this.getWeekFieldName(index),
        cellRenderer: this.weekBoxRenderer.bind(this),
        cellClass: 'pr-2 pl-2',
        headerClass: 'pr-2 pl-2',
        filter: false,
        sortable: false,
        resizable: false,
        floatingFilter: false,
        suppressMovable: true,
        suppressMenu: true,
        maxWidth: 50,
        autoSize: false
      }));

      // Update column definitions
      this.gridOptions.columnDefs = [...fixedColumns, ...weekColumns];

      // Apply to grid
      this.grid.gridApi.setColumnDefs(this.gridOptions.columnDefs);
    }
  }

  /**
   * Update week dates and refresh grid after calendar start date change
   */
  private updateWeekDatesAndRefreshGrid(newSchedCalStartDate: string): void {
    // Update stored site data
    if (this.siteData) {
      this.siteData.sched_cal_start_date = newSchedCalStartDate;
    }

    // Recalculate week dates
    this.weekDates = this.calculateWeekDates(newSchedCalStartDate);

    // Refresh grid columns with new dates
    this.refreshGridColumns();
  }

  weekBoxRenderer(params: any) {
    if ([null, undefined, ''].includes(params.value)) return '';

    // if the current column date is in the past, set the box color to #ccc
    const currentDate = new Date().toISOString().split('T')[0];
    const columnDate = params.column.colDef.headerValue;
    const boxColor = columnDate < currentDate ? '#ccc' : '#007bff';

    if (params.node.rowPinned === 'top') {
      const weekTotal = params.value;
      const maxTotal = this.maxWeekTotal;
      const height = maxTotal > 0 ? (weekTotal / maxTotal) * 100 : 0;

      return `
        <div style="display: inline-block; text-align: center;">
          <div style="margin-bottom: 1px; font-weight: bold; color: #333;">${params.value > 0 ? params.value : '-'}</div>
          <div style="height: 100px; width: 20px; display: flex; align-items: flex-end;">
            <div style="width: 100%; height: ${height}%; background-color: ${boxColor}; border-radius: 4px 4px 0 0;"></div>
          </div>
        </div>
      `;
    }

    return `<span style="background-color:${boxColor};color:white;padding:6px; border-radius:4px;text-align:center;">${params.value}</span>`;
  }

  /**
   */
  async ngOnInit(): Promise<void> {
    this.breadCrumbService.set(
      [
        {
          name: this.translate.instant('navigations.breadcrumbs.modules'),
          link: '/modules'
        },
        {
          name: this.translate.instant('navigations.breadcrumbs.schedule'),
          link: '/routing/schedule'
        }
      ],
      2
    );

    // Initialize grid options without week columns - they will be added after loading items
    this.gridOptions = {
      getRowId: (row: any) => row.data.id,
      suppressRowClickSelection: false,
      rowSelection: 'multiple',
      rowHeight: 30,
      headerHeight: 30,
      animateRows: true,
      floatingFiltersHeight: 30,
      enableCellTextSelection: false,
      suppressContextMenu: true,
      getRowHeight: (params) => (params.node.rowPinned === 'top' ? 140 : 30),
      onFilterChanged: this.onFilterChanged.bind(this),
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.translate.instant('headers.routing.plan.plan'),
          field: 'name',
          filter: 'agTextColumnFilter',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          pinned: 'left'
        },

        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.frequency.frequency'
          ),
          field: 'frequency',
          pinned: 'left'
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.state.state'
          ),
          field: 'state',
          pinned: 'left'
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.labor.labor'
          ),
          field: 'labour',
          pinned: 'left'
        },
        {
          headerName: this.translate.instant('headers.routing.plan.workcenter'),
          field: 'workcenter',
          pinned: 'left'
        }
      ]
    };
    await this.loadItems();
  }

  /**
   */
  async loadItems(silent = false): Promise<void> {
    if (!silent) {
      this.isLoading = true;
    }

    this.items = await this.data.get('api/schedules/from_site').toPromise();

    // Transform the new API response structure to match expected format
    this.items = this.items.map((item: any) => {
      const transformedItem = { ...item };

      // Initialize all week fields to null
      for (let i = 1; i <= 52; i++) {
        transformedItem[`week_${i}`] = null;
      }

      // Map schedule data to week fields
      if (item.schedule && Array.isArray(item.schedule)) {
        item.schedule.forEach((scheduleItem: any) => {
          if (scheduleItem.week_number && scheduleItem.duration) {
            const weekField = `week_${scheduleItem.week_number}`;
            transformedItem[weekField] = scheduleItem.duration;
          }
        });
      }

      return transformedItem;
    });

    // Get site data to determine calendar start date
    const siteId = this.auth.getSiteId();
    let schedCalStartDate = null;

    if (siteId) {
      try {
        const site = await this.data.detail('api/sites', siteId).toPromise();
        this.siteData = site; // Store site data for reuse
        if (site?.sched_cal_start_date) {
          schedCalStartDate = site.sched_cal_start_date.substring(0, 10);
        }
      } catch (error) {
        console.warn('Could not fetch site data');
      }
    }

    // if schedCalStartDate is null, show toastr error
    if (!schedCalStartDate) {
      this.toastr.showError('messages.cal_start_date_not_set');
      return;
    }

    // Calculate week dates
    this.weekDates = this.calculateWeekDates(schedCalStartDate);

    // Add week columns to grid options
    if (this.gridOptions && this.gridOptions.columnDefs) {
      // Refresh the grid columns
      this.refreshGridColumns();
    }

    // Calculate weekly totals using the common method
    this.updateWeeklyTotalsWithFilters();

    this.grid?.expandAll();
    this.grid?.uncheckAll();
    if (!silent) {
      this.isLoading = false;
    }
  }

  onSelectionChanged(event: any): void {
    this.selectedNodes = event;
    this.selectedPlan = this.selectedNodes.map((item) => item.data.id);
  }

  onFilterChanged(filterModel: any): void {
    // Recalculate weekly totals based on filtered data
    this.updateWeeklyTotalsWithFilters();
  }

  /**
   * Calculate weekly totals from a given array of items
   * @param items Array of items to calculate totals from
   * @returns Object containing the top row data and max week total
   */
  private calculateWeeklyTotals(items: any[]): {
    topRow: any;
    maxWeekTotal: number;
  } {
    // Create the top row structure with all week fields initialized to 0
    const topRow: any = {
      name: 'Weekly Totals (hrs)',
      ...this.weekDates
        .map((_, index) => this.getWeekFieldName(index))
        .reduce((acc: any, key: any) => {
          acc[key] = 0;
          return acc;
        }, {})
    };

    // Sum up the weekly values from the provided items
    items.forEach((item: any) => {
      for (let i = 1; i <= 52; i++) {
        topRow[`week_${i}`] += item[`week_${i}`] || 0;
      }
    });

    // Calculate max week total for scaling
    const weeklyTotals: number[] = Object.values(topRow).filter((value) =>
      isNumber(value)
    ) as number[];
    const maxWeekTotal = Math.max(...weeklyTotals, 0); // Ensure it's not negative

    return { topRow, maxWeekTotal };
  }

  /**
   * Update weekly totals based on currently filtered data
   */
  private updateWeeklyTotalsWithFilters(): void {
    if (!this.grid?.gridApi) {
      return;
    }

    // Get all visible (filtered) rows
    const visibleRows: any[] = [];
    this.grid.gridApi.forEachNodeAfterFilter((node: any) => {
      if (node.data && !node.rowPinned) {
        visibleRows.push(node.data);
      }
    });

    // Calculate weekly totals using the common method
    const { topRow, maxWeekTotal } = this.calculateWeeklyTotals(visibleRows);

    // Update the component properties
    this.maxWeekTotal = maxWeekTotal;
    this.topRow = [{ ...topRow }];
  }

  /**
   * Store current selections and restore them after data reload
   */
  private async loadItemsWithSelectionRetention(): Promise<void> {
    // Store current selections
    const selectedIds = [...this.selectedPlan];

    // Load items
    await this.loadItems(true); // silent = true to avoid loading indicator

    // Restore selections after a short delay to ensure grid is ready
    setTimeout(() => {
      if (selectedIds.length > 0 && this.grid?.gridApi) {
        this.grid.gridApi.forEachNode((node: any) => {
          if (selectedIds.includes(node.data.id)) {
            node.setSelected(true);
          }
        });
        // Update the selected nodes and plan arrays
        this.selectedNodes = this.grid.gridApi.getSelectedNodes();
        this.selectedPlan = this.selectedNodes.map((item) => item.data.id);
      }
    }, 100);
  }

  shiftLeft(): void {
    this.isLoading = true;
    this.data
      .post('api/schedules/shift', {
        direction: 'left',
        plan_ids: this.selectedPlan
      })
      .subscribe(() => {
        this.isLoading = false;
        this.toastr.showSuccess('Shift left completed');
        this.loadItemsWithSelectionRetention();
      });
  }

  shiftRight(): void {
    this.isLoading = true;
    this.data
      .post('api/schedules/shift', {
        direction: 'right',
        plan_ids: this.selectedPlan
      })
      .subscribe(() => {
        this.isLoading = false;
        this.toastr.showSuccess('Shift right completed');
        this.loadItemsWithSelectionRetention();
      });
  }

  autoBalance(): void {
    this.isLoading = true;
    this.data
      .post('api/schedules/auto_balance', {
        plan_ids: this.selectedPlan
      })
      .subscribe(() => {
        this.isLoading = false;
        this.toastr.showSuccess('Auto-balance completed');
        this.loadItemsWithSelectionRetention();
      });
  }

  resetCalendar(): void {
    this.isLoading = true;
    this.data
      .post('api/schedules/reset_calendar', {
        plan_ids: this.selectedPlan
      })
      .subscribe(
        () => {
          this.isLoading = false;
          this.toastr.showSuccess('Calendar reset completed');
          this.loadItemsWithSelectionRetention();
        },
        (error) => {
          this.isLoading = false;
          this.toastr.showError('Failed to reset calendar');
          console.error('Reset calendar error:', error);
        }
      );
  }

  openCalendarStartDate(): void {
    if (!this.calendarStartDateTpl) {
      return;
    }

    const showModal = () => {
      this.bsModalRef = this.bsModalService.show(
        this.calendarStartDateTpl as TemplateRef<any>,
        {
          class: 'modal-sm'
        }
      );
    };

    // Default to blank
    this.selectedCalendarDate = undefined;

    const siteId = this.auth.getSiteId();
    if (!siteId) {
      showModal();
      return;
    }

    // Use stored site data instead of making another API call
    if (this.siteData?.sched_cal_start_date) {
      this.selectedCalendarDate = this.siteData.sched_cal_start_date.substring(
        0,
        10
      );
    }

    showModal();
  }

  confirmCalendarStartDate(): void {
    if (!this.selectedCalendarDate) {
      this.toastr.showError('messages.error');
      return;
    }
    this.submitSchedCalStartDate(this.selectedCalendarDate);
    this.bsModalRef?.hide();
  }

  submitSchedCalStartDate(date: string): void {
    this.isLoading = true;
    const iso = new Date(`${date}T00:00:00Z`).toISOString();
    const payload = { sched_cal_start_date: iso };
    this.data.post('api/sites/sched_cal_start_date', payload).subscribe(
      (response) => {
        this.isLoading = false;
        this.toastr.showSuccess('messages.save');

        // Use the response data if available, otherwise use the submitted date
        const newSchedCalStartDate = response?.sched_cal_start_date || iso;

        // Efficiently update week dates and refresh grid without reloading all data
        this.updateWeekDatesAndRefreshGrid(newSchedCalStartDate);
      },
      () => {
        this.isLoading = false;
        this.toastr.showError('messages.error');
      }
    );
  }

  openNativeDatePicker(inputEl: HTMLInputElement): void {
    const anyInput = inputEl as any;
    if (anyInput && typeof anyInput.showPicker === 'function') {
      anyInput.showPicker();
      return;
    }
    inputEl.focus();
    try {
      inputEl.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      inputEl.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    } catch (_) {
      // ignore fallback errors
    }
  }
}
